<template>
  <v-app class="app-background">
    <f2gsnackbar ref="f2gsnackbar" />
    <v-main v-if="$i18n">
      <!-- loading screen when checking token -->
      <div class="loadingToken" v-if="checkingToken">
        <div class="lds-facebook">
          <div></div>
          <div></div>
          <div></div>
        </div>
      </div>
      <!-- views when not checking token -->
      <div v-else>
        <div
          v-if="showRefreshBanner"
          class="absolute top-0 left-0 w-full flex"
          style="
            z-index: 100;
            background-color: #f5b540;
            height: 38px;
            align-items: center;
          "
        >
          <div class="ml-4 flex" style="align-items: center">
            <span>{{ $t("refreshBanner") }}</span>
            <v-btn
              color="black"
              small
              outlined
              class="rounded ml-2 mb-2 mt-1"
              @click="refreshWindow"
              >{{ $t("common.update") }}</v-btn
            >
            <span
              style="
                position: absolute;
                right: 7px;
                margin-top: 7px;
                margin-right: 8px;
                cursor: pointer;
              "
              @click="showRefreshBanner = false"
              >X</span
            >
          </div>
        </div>
        <router-view />
      </div>
    </v-main>
  </v-app>
</template>

<script>
import f2gsnackbar from "@/flit2go-web-core/components/ui/f2g-snackbar";
import menuStructureFile from "@/base/menuStructure/menuStructure.js";
import { EventBus } from "@/flit2go-web-core/util/event-bus.js";
import initZohoSalesIQ from "@/util/initZohoSalesIQ.js";
import pagesense from "@/services/pagesense.js";
import zohoDeskAsap from "@/flit2go-web-core/services/zohoDeskAsapService.js";
import {
  HELP_CENTER_LOGIN,
  HELP_CENTER_UNAVAILABLE,
} from "@/base/routesInterface";
import Vue from "vue";

export default {
  name: "app",
  components: { f2gsnackbar },
  data: () => ({
    checkingToken: true,
    docsPageURL: process.env.VUE_APP_DOCS_URL,
    showRefreshBanner: false,
  }),
  created() {
    EventBus.$on("showRefreshBanner", this.showRefreshBannerAction);
    // Every logout path (toolbar, expired token...) goes through AUTH_LOGOUT
    this.$store.subscribeAction({
      after: (action) => {
        if (action.type === "auth/AUTH_LOGOUT") {
          pagesense.resetUser();
          zohoDeskAsap.logout();
        }
      },
    });
    zohoDeskAsap.setUnavailableHandler(() => {
      if (this.$route.path !== HELP_CENTER_UNAVAILABLE) {
        this.$router.push(HELP_CENTER_UNAVAILABLE).catch(() => {});
      }
    });
    zohoDeskAsap.init();
  },
  mounted() {
    // Check stored token on reload
    this.$root.f2gsnackbar = this.$refs.f2gsnackbar;
    this.initShowMessages();
    const token = localStorage.getItem("user-token");

    const params = new URLSearchParams();
    try {
      const userProfile = localStorage.getItem("user-profile");
      if (userProfile) {
        const parsedProfile = JSON.parse(userProfile);
        if (parsedProfile?.username) {
          params.append("username", parsedProfile.username);
        }
      }
    } catch {
      // Do nothing
    }

    if (token) {
      this.checkingToken = true;
      this.$store
        .dispatch("auth/AUTH_CHECK_TOKEN", { token, params })
        .then(() => {
          initZohoSalesIQ(this.$store.state.auth.userProfile);
          pagesense.identifyUser(this.$store.state.auth.userProfile);
          zohoDeskAsap.identifyUser();
          this.getPerms();
        })
        .catch(this.redirectToLogin);
    } else {
      this.redirectToLogin();
    }

    // Set language to user language or browser language
    let language = localStorage.getItem("user-language");
    localStorage.setItem("user-language", language);
    if (!language) {
      let person = JSON.parse(localStorage.getItem("user-profile"));
      if (person && person.language) {
        language = person.language;
      } else {
        language = navigator.language;
      }
    }
    if (language.includes("_")) language = language.split("_")[0];
    this.$store.dispatch("settings/CHANGE_LANG", { language: language });
  },
  methods: {
    loadLanguages() {
      //load languages
      this.$store.dispatch("settings/getLanguages").finally(() => {
        this.checkingToken = false;
      });
    },
    redirectToLogin() {
      if (this.$route.path === HELP_CENTER_LOGIN) {
        // Keep the way back to the Zoho help center portal sign-in after logging in again
        this.$router.push({
          path: "/login",
          query: { redirect: this.$route.fullPath },
        });
      } else if (this.$route.path !== "/login") {
        this.$router.push("/login");
      }
      this.checkingToken = false;
    },
    showRefreshBannerAction() {
      this.showRefreshBanner = true;
    },
    refreshWindow() {
      this.showRefreshBanner = false;
      navigator.serviceWorker.getRegistrations();
      window.location.reload(true);
    },
    getPerms() {
      this.$store.dispatch("auth/getSetRoleSchema").finally(() => {
        if (this.$route.path === "/login") {
          this.$router.push(menuStructureFile.getFirstLinkAllowed());
        } else if (this.$route.path !== HELP_CENTER_LOGIN) {
          // The help center portal login page is open to every authenticated user
          if (!menuStructureFile.hasReadPermission(this.$route.meta.code)) {
            this.$router.push(menuStructureFile.getFirstLinkAllowed());
          }
        }
        setTimeout(this.loadLanguages, 500);
      });
    },
    initShowMessages() {
      Vue.prototype.$showMessage = {
        success: (message) =>
          this.$root.f2gsnackbar.show({
            text: message,
            code: "success",
            show: true,
          }),
        info: (message) =>
          this.$root.f2gsnackbar.show({
            text: message,
            code: "info",
            show: true,
          }),
        error: (message) =>
          this.$root.f2gsnackbar.show({
            text: message,
            code: "error",
            show: true,
          }),
        warning: (message) =>
          this.$root.f2gsnackbar.show({
            text: message,
            code: "warning",
            show: true,
          }),
        errorGeneric: () =>
          this.$root.f2gsnackbar.show({
            text: this.$t(this.$CONSTANTS.ERROR.GENERIC),
            code: "error",
            show: true,
          }),
        showError: (message) => {
          let error;
          if (message.response && message.response.data) {
            error = message.response.data.errorMessage
              ? message.response.data.errorMessage
              : message.response.data;
          } else {
            error = this.$t(this.$CONSTANTS.ERROR.GENERIC);
          }
          this.$root.f2gsnackbar.show({
            text: error,
            code: "error",
            show: true,
          });
        },
      };
    },
  },
};
</script>

<style lang="scss" scoped>
.app-background {
  background-color: white !important;
}
.loadingToken {
  position: fixed;
  top: 0;
  left: 0;
  bottom: 0;
  right: 0;
  z-index: 20;
  background-color: white;
  display: flex;
  flex-direction: row;
  justify-content: center;
  align-items: center;

  .lds-facebook {
    display: inline-block;
    position: relative;
    width: 64px;
    height: 64px;
  }

  .lds-facebook div {
    display: inline-block;
    position: absolute;
    left: 6px;
    width: 13px;
    background: "#ff0800";
    animation: lds-facebook 1.2s cubic-bezier(0, 0.5, 0.5, 1) infinite;
  }

  .lds-facebook div:nth-child(1) {
    left: 6px;
    animation-delay: -0.24s;
  }

  .lds-facebook div:nth-child(2) {
    left: 26px;
    animation-delay: -0.12s;
  }

  .lds-facebook div:nth-child(3) {
    left: 45px;
    animation-delay: 0;
  }

  @keyframes lds-facebook {
    0% {
      top: 6px;
      height: 51px;
    }
    50%,
    100% {
      top: 19px;
      height: 26px;
    }
  }
}
</style>
